import PropTypes from "prop-types";
import { useContext } from "react";
import classNames from "classnames";
import ReducedPrice from "../ReducedPrice";
import OriginalPrice from "../OriginalPrice";
import styles from "./index.css";
import getPriceInfo from "./getPriceInfo";
import { withTranslation } from "../../../../../translation";
import ProductDeal from "../../../../ProductPrice/ProductDeal";
import TileContext from "../../../../../TileContext";
import { TILE_SIZES } from "../../../../../../helpers/constants";

const { TILE_SIZE_TWO_BY_ONE, TILE_SIZE_TWO_BY_TWO } = TILE_SIZES;

const shouldDisplayFrom = (hasMultiplePrices, reducedPrice) =>
  hasMultiplePrices && !reducedPrice;

const renderOriginalPrice = (
  showOriginalPrice,
  price,
  isPromotionalDiscount,
  isMarkedDown,
  isOutlet,
  hasMultiplePrices,
  reducedPrice,
) => {
  if (!showOriginalPrice) return null;

  return (
    <OriginalPrice
      price={price}
      isMarkedDown={isPromotionalDiscount || isMarkedDown}
      isOutlet={isOutlet}
      shouldDisplayFrom={shouldDisplayFrom(hasMultiplePrices, reducedPrice)}
      isPromotionalDiscount={isPromotionalDiscount}
    />
  );
};

const renderMarkdownPrice = (
  hasBothMarkdownAndPromotional,
  reducedPrice,
  hasMultiplePrices,
) => {
  if (!hasBothMarkdownAndPromotional || !reducedPrice) return null;

  return (
    <ReducedPrice
      isMarkedDown={true}
      reducedPrice={reducedPrice}
      hasMultiplePrices={hasMultiplePrices}
      isPromotionalDiscount={false}
      isStrikethroughInPromoContext={true}
    />
  );
};

const renderPromotionalDiscount = (
  isPromotionalDiscount,
  discountBenefitValue,
  hasMultiplePrices,
  discountCode,
  discountedPrice,
) => {
  if (!isPromotionalDiscount || !discountBenefitValue) return null;

  return (
    <ReducedPrice
      isMarkedDown={false}
      discountBenefitValue={discountBenefitValue}
      hasMultiplePrices={hasMultiplePrices}
      isPromotionalDiscount={true}
      discountCode={discountCode}
      discountedPrice={discountedPrice}
    />
  );
};

const renderRegularMarkdown = (
  isPromotionalDiscount,
  reducedPrice,
  isMarkedDown,
  hasMultiplePrices,
) => {
  if (isPromotionalDiscount || !reducedPrice) return null;

  return (
    <ReducedPrice
      isMarkedDown={isMarkedDown}
      reducedPrice={reducedPrice}
      hasMultiplePrices={hasMultiplePrices}
      isPromotionalDiscount={false}
    />
  );
};

const ProductPrice = ({
  price,
  isSale,
  isOutlet,
  reducedPrice,
  hasMultiplePrices,
  labelledById,
  formatTranslation,
  euOmnibusEnabled,
  deal,
  dealAria,
  discountBenefitValue,
  discountCode,
  discountedPrice,
}) => {
  const isMarkedDown = isSale || isOutlet;
  const { tileSize } = useContext(TileContext);
  const isPromotionalDiscount = discountBenefitValue != null;
  const hasBothMarkdownAndPromotional = isMarkedDown && isPromotionalDiscount;

  const showOriginalPrice =
    !hasBothMarkdownAndPromotional && (euOmnibusEnabled ? !isMarkedDown : true);

  const ariaLabel = getPriceInfo({
    price,
    discountBenefitValue: isPromotionalDiscount ? discountBenefitValue : null,
    reducedPrice,
    isOutlet,
    hasMultiplePrices,
    formatTranslation,
    euOmnibusEnabled,
    isPromotionalDiscount,
    discountCode,
    discountedPrice: isPromotionalDiscount ? discountedPrice : null,
    markdownPrice: hasBothMarkdownAndPromotional ? reducedPrice : null,
  });

  return (
    <p
      id={labelledById}
      aria-hidden={true}
      aria-label={ariaLabel}
      className={classNames(styles.container, {
        [styles.twoByOne]: tileSize === TILE_SIZE_TWO_BY_ONE,
        [styles.twoByTwo]: tileSize === TILE_SIZE_TWO_BY_TWO,
      })}
    >
      {renderOriginalPrice(
        showOriginalPrice,
        price,
        isPromotionalDiscount,
        isMarkedDown,
        isOutlet,
        hasMultiplePrices,
        reducedPrice,
      )}

      {renderMarkdownPrice(
        hasBothMarkdownAndPromotional,
        reducedPrice,
        hasMultiplePrices,
      )}

      {renderPromotionalDiscount(
        isPromotionalDiscount,
        discountBenefitValue,
        hasMultiplePrices,
        discountCode,
        discountedPrice,
      )}

      {renderRegularMarkdown(
        isPromotionalDiscount,
        reducedPrice,
        isMarkedDown,
        hasMultiplePrices,
      )}

      {deal && !isPromotionalDiscount && (
        <ProductDeal
          deal={deal}
          dealAria={dealAria}
          labelledById={labelledById}
        />
      )}
    </p>
  );
};

ProductPrice.propTypes = {
  price: PropTypes.string.isRequired,
  isSale: PropTypes.bool,
  isOutlet: PropTypes.bool,
  reducedPrice: PropTypes.string,
  deal: PropTypes.string,
  dealAria: PropTypes.string.isRequired,
  hasMultiplePrices: PropTypes.bool,
  labelledById: PropTypes.string.isRequired,
  formatTranslation: PropTypes.func.isRequired,
  euOmnibusEnabled: PropTypes.bool.isRequired,
  discountBenefitValue: PropTypes.string,
  discountCode: PropTypes.string,
  discountedPrice: PropTypes.string,
};

ProductPrice.defaultProps = {
  hasMultiplePrices: false,
  isOutlet: false,
  reducedPrice: null,
  isSale: false,
  discountBenefitValue: null,
  discountCode: null,
  discountedPrice: null,
};

export default withTranslation(ProductPrice);
